import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { RecruitmentCandidate, RecruitmentChangeHistory, RecruitmentStatus, ResumeFile } from './recruitment.model';
import {
  INTERVIEW_ROUND_OPTIONS,
  INTERVIEW_ROUND_ORDER,
  INTERVIEWER_STATUS_OPTIONS,
  POST_OFFER_STATUS_OPTIONS,
  PRE_OFFER_STATUS_OPTIONS,
  RECOMMENDATION_STATUS_OPTIONS,
  RECRUITMENT_MESSAGES,
  RECRUITMENT_STATUS_OPTIONS,
  STATUS_ORDER,
  DEFAULT_SELECTION_PERCENTAGE
} from './recruitment.constants';
import { RecruitmentService, RecruitmentStats } from '../../services/recruitment.service';
import { UserService } from '../../services/user.service';
import { IdName } from '../../dtos/IdName';
import { Department } from '../user/department.model';
import { UserDTO } from '../user/user.model';
import { DepartmentEnum } from '../../enums/department.enum';
import { RoleEnum, RoleNameEnum } from '../../enums/role.enum';

type RecruitmentView = 'upload' | 'review' | 'export' | 'documents';

@Component({
  selector: 'app-recruitment',
  templateUrl: './recruitment.component.html',
  styleUrls: ['./recruitment.component.css'],
  standalone: false
})
export class RecruitmentComponent implements OnInit, OnDestroy {
  @ViewChild('candidateListSection') private candidateListSection?: ElementRef<HTMLElement>;

  activeView: RecruitmentView = 'upload';
  isResumeUploadExpanded = false;
  uploadedFiles: ResumeFile[] = [];
  selectedResumeFiles: File[] = [];
  candidates: RecruitmentCandidate[] = [];
  filteredCandidates: RecruitmentCandidate[] = [];
  changeHistory: RecruitmentChangeHistory[] = [];
  candidateHistory: RecruitmentChangeHistory[] = [];
  selectedCandidate?: RecruitmentCandidate;
  selectedDocumentCandidate?: RecruitmentCandidate;
  totalRecords = 0;
  stats: RecruitmentStats = {
    totalCandidates: 0,
    reviewed: 0,
    interviewed: 0,
    offered: 0,
    rejected: 0
  };
  candidateForm: FormGroup;
  displayedColumns: string[] = ['index', 'name', 'email', 'contactNumber', 'experience', 'status', 'actions'];
  previewColumns: string[] = ['candidateName', 'sourceOfResume', 'department', 'status', 'interviewRound', 'interviewerName', 'overallPercentage', 'recommendationStatus', 'remarks', 'responsiblePerson', 'createdDate'];
  searchText = '';
  exportSearchText = '';
  statusFilter = 'All';
  roundFilter = 'All';
  candidatePageIndex = 0;
  candidatePageSize = 10;
  exportPageIndex = 0;
  exportPageSize = 10;
  hrUsers: IdName[] = [];
  employeeUsers: IdName[] = [];
  hrDepartmentUsers: IdName[] = [];
  departmentUsers: IdName[] = [];
  departments: Department[] = [];
  readonly statusOptions = RECRUITMENT_STATUS_OPTIONS;
  readonly recommendationStatusOptions = RECOMMENDATION_STATUS_OPTIONS;
  readonly interviewRoundOptions = INTERVIEW_ROUND_OPTIONS;
  private readonly subscriptions = new Subscription();
  private shouldScrollCandidateListOnLoad = true;
  uploadMode: 'single' | 'multiple' = 'multiple';

  constructor(
    private recruitmentService: RecruitmentService,
    private userService: UserService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private snackBar: MatSnackBar
  ) {
    this.candidateForm = this.fb.group({
      reviewed: [false],
      reviewedBy: [''],
      sourceOfResume: [''],
      departmentId: [''],
      cvUploadedOn: [''],
      reviewedOn: [''],
      interviewerName: [''],
      interviewRound: [''],
      technicalSkills: [''],
      overallPercentage: ['', [Validators.min(0), Validators.max(100), Validators.pattern(/^\d{1,3}$/)]],
      recommendationStatus: [''],
      status: ['PENDING'],
      remarks: ['']
    });
  }

  ngOnInit(): void {
    this.loadCandidates();
    this.loadStats();
    this.loadReviewUsers();
    this.subscriptions.add(
      this.candidateForm.get('departmentId')?.valueChanges.subscribe((departmentId) => this.onDepartmentChange(departmentId))
    );
    this.subscriptions.add(
      this.candidateForm.get('status')?.valueChanges.subscribe((status) => this.onStatusChange(status as RecruitmentStatus))
    );
    this.subscriptions.add(
      this.candidateForm.get('interviewRound')?.valueChanges.subscribe((round) => this.onInterviewRoundChange(round))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get totalCandidates(): number {
    return this.stats.totalCandidates;
  }

  get reviewedCount(): number {

    return this.stats.reviewed;
  }

  get interviewedCount(): number {
    return this.stats.interviewed;
  }

  get offeredCount(): number {
    return this.stats.offered;
  }

  get rejectedCount(): number {
    return this.stats.rejected;
  }

  get pagedChangeHistory(): RecruitmentChangeHistory[] {
    return this.paginate(this.filteredChangeHistory, this.exportPageIndex, this.exportPageSize);
  }

  get filteredChangeHistory(): RecruitmentChangeHistory[] {
    const search = this.exportSearchText.trim().toLowerCase();
    if (!search) {
      return this.changeHistory;
    }

    return this.changeHistory.filter((row) => [
      row.candidateName,
      this.getHistoryField(row, 'Source of Resume/CV'),
      row.status,
      this.getHistoryField(row, 'Interview Round'),
      this.getHistoryField(row, 'Interviewer Name'),
      this.getHistoryField(row, 'Overall Percentage'),
      this.getHistoryField(row, 'Recommendation Status'),
      this.getHistoryRemarks(row),
      this.getUserNameById(row.responsiblePerson),
      this.getHistoryChangedOn(row)
    ].some((value) => `${value ?? ''}`.toLowerCase().includes(search)));
  }

  get candidateStart(): number {
    return this.totalRecords === 0 ? 0 : this.candidatePageIndex * this.candidatePageSize + 1;
  }

  get candidateEnd(): number {
    return Math.min((this.candidatePageIndex + 1) * this.candidatePageSize, this.totalRecords);
  }

  get isPostOfferFlow(): boolean {
    return this.isPostOfferStatus(this.selectedCandidate?.status);
  }

  get isOfferedOrLater(): boolean {
    return this.isPostOfferStatus(this.selectedCandidate?.status);
  }

  get isHrUser(): boolean {
    const user = this.currentUser();
    const roleId = Number(user?.roleId ?? sessionStorage.getItem('roleId'));
    const role = `${user?.role ?? user?.roleName ?? sessionStorage.getItem('role') ?? ''}`.trim().toLowerCase();
    return roleId === RoleEnum.HR || role === RoleNameEnum.HR.toLowerCase();
  }

  get canUseRecruitmentAdminActions(): boolean {
    return this.isHrUser;
  }

  get isInterviewerEditMode(): boolean {
    return !this.isHrUser;
  }

  get showInterviewerFeedbackFields(): boolean {
    if (this.isInterviewerEditMode) {
      return true;
    }

    return ['Screening', 'Final Interaction', 'HR Round'].includes(this.candidateForm.get('interviewRound')?.value);
  }

  get showInterviewerNameField(): boolean {
    return this.candidateForm.get('interviewRound')?.value !== 'Screening';
  }

  get showInterviewDateTimeField(): boolean {
    return this.candidateForm.get('interviewRound')?.value !== 'Screening';
  }

  showView(view: RecruitmentView): void {
    if (!this.canUseRecruitmentAdminActions && view !== 'upload' && view !== 'review') {
      return;
    }
    this.activeView = view;
    if (view === 'upload') {
      this.scrollToCandidateList();
    }
    if (view === 'export') {
      this.exportPageIndex = 0;
      this.loadChangeHistory();
    }
  }

  onResumeSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.selectedResumeFiles = [...this.selectedResumeFiles, ...files];
      this.refreshUploadedFiles();
      input.value = '';
    }
  }

  removeResumeFile(fileId: number): void {
    const index = this.uploadedFiles.findIndex((file) => file.id === fileId);
    if (index >= 0) {
      this.selectedResumeFiles.splice(index, 1);
      this.refreshUploadedFiles();
    }
  }

  onDocumentsSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0 && this.selectedDocumentCandidate) {
      const candidateId = this.selectedDocumentCandidate.id;
      this.subscriptions.add(
        this.recruitmentService.uploadDocuments(candidateId, Array.from(input.files)).subscribe((documents) => {
          if (this.selectedDocumentCandidate) {
            this.selectedDocumentCandidate = {
              ...this.selectedDocumentCandidate,
              msaDocuments: [...this.selectedDocumentCandidate.msaDocuments, ...documents]
            };
          }
          this.loadCandidates();
        })
      );
      input.value = '';
    }
  }

  removeMsaDocument(documentId: number): void {
    if (!this.selectedDocumentCandidate) {
      return;
    }
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    const candidateId = this.selectedDocumentCandidate.id;
    this.subscriptions.add(
      this.recruitmentService.deleteDocument(candidateId, documentId).subscribe(() => {
        if (this.selectedDocumentCandidate) {
          this.selectedDocumentCandidate = {
            ...this.selectedDocumentCandidate,
            msaDocuments: this.selectedDocumentCandidate.msaDocuments.filter((document) => document.id !== documentId)
          };
        }
        this.loadCandidates();
      })
    );
  }

  downloadMsaDocument(file: ResumeFile): void {
    if (!this.selectedDocumentCandidate) {
      return;
    }

    const candidateId = this.selectedDocumentCandidate.id;
    this.subscriptions.add(
      this.recruitmentService.downloadDocument(candidateId, file.id).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = window.document.createElement('a');
          link.href = url;
          link.download = file.name || 'document';
          link.click();
          window.URL.revokeObjectURL(url);
        },
        error: () => this.showMessage('Unable to download document.')
      })
    );
  }

  resetUploads(): void {
    this.selectedResumeFiles = [];
    this.uploadedFiles = [];
  }

  private refreshUploadedFiles(): void {
    this.uploadedFiles = this.selectedResumeFiles.map((file, index) => ({
      id: index + 1,
      name: file.name,
      size: this.formatFileSize(file.size),
      uploadedOn: '',
      status: 'Uploaded'
    }));
  }

  uploadAndParse(): void {
    if (this.selectedResumeFiles.length === 0) {
      return;
    }

    this.subscriptions.add(
      this.recruitmentService.uploadResumes(this.selectedResumeFiles).subscribe(() => {
        this.uploadedFiles = this.uploadedFiles.map((file) => ({ ...file, status: 'Parsed' }));
        this.selectedResumeFiles = [];
        this.loadCandidates();
        this.loadStats();
        this.showMessage('Resume parsed successfully.');
        this.showView('upload');
      })
    );
  }

  applyFilters(): void {
    this.candidatePageIndex = 0;
    this.loadCandidates();
  }

  clearFilters(): void {
    this.searchText = '';
    this.statusFilter = 'All';
    this.roundFilter = 'All';
    this.candidatePageIndex = 0;
    this.loadCandidates();
  }

  setRoundFilter(round: string): void {
    this.roundFilter = round;
    this.exportSearchText = round === 'All' ? '' : round;
    this.exportPageIndex = 0;
  }

  onCandidatePageChange(event: PageEvent): void {
    this.candidatePageIndex = event.pageIndex;
    this.candidatePageSize = event.pageSize;
    this.loadCandidates();
  }

  onExportPageChange(event: PageEvent): void {
    this.exportPageIndex = event.pageIndex;
    this.exportPageSize = event.pageSize;
  }

  applyExportSearch(): void {
    this.exportPageIndex = 0;
  }

  onOverallPercentageInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(0, 3);
    input.value = value;
    this.candidateForm.get('overallPercentage')?.setValue(value, { emitEvent: false });
  }

    
  toggleResumeUpload(): void {
    this.isResumeUploadExpanded = !this.isResumeUploadExpanded;
  }

  openCandidate(candidate: RecruitmentCandidate): void {
    this.selectedCandidate = candidate;
    this.candidateHistory = [];
    this.patchCandidateForm(candidate);
    this.applyCandidateFormAccess();
    this.loadCandidateDetails(candidate.id);
    this.loadCandidateHistory(candidate.id);
    this.showView('review');
  }

  private patchCandidateForm(candidate: RecruitmentCandidate): void {
    const isFreshPostOffer = candidate.status === 'OFFERED';
    const assignedHr = candidate.reviewedBy || (this.isHrUser ? this.currentUserName() : '');
    this.ensureHrUserOption(assignedHr);
    this.ensureCandidateDepartmentOption(candidate);
    this.candidateForm.patchValue({
      reviewed: candidate.reviewed,
      reviewedBy: assignedHr,
      sourceOfResume: candidate.sourceOfResume,
      departmentId: this.resolveCandidateDepartmentId(candidate),
      cvUploadedOn: this.toDateControlValue(candidate.cvUploadedOn),
      reviewedOn: this.toDateTimeControlValue(candidate.reviewedOn),
      interviewerName: isFreshPostOffer ? '' : candidate.interviewerName === '-' ? '' : candidate.interviewerName,
      interviewRound: candidate.interviewRound === '-' ? '' : candidate.interviewRound,
      technicalSkills: candidate.technicalSkills,
      overallPercentage: candidate.overallPercentage ?? '',
      recommendationStatus: candidate.recommendationStatus,
      status: candidate.status,
      remarks: ''
    }, { emitEvent: false });
  }

  saveCandidate(): void {
    if (!this.selectedCandidate) {
      return;
    }

    this.applyFeedbackValidators();
    if (this.candidateForm.invalid) {
      this.candidateForm.markAllAsTouched();
      if (this.candidateForm.get('recommendationStatus')?.hasError('required')) {
        this.showMessage(RECRUITMENT_MESSAGES.recommendationRequired);
        return;
      }
      if (this.candidateForm.get('remarks')?.hasError('required')) {
        this.showMessage(RECRUITMENT_MESSAGES.remarksRequired);
        return;
      }
      return;
    }

    const formValue = this.candidateForm.getRawValue();
    // if (!this.isOverallPercentageValid()) {   need to add validaton 
    //   return;
    // }
    if (this.showInterviewerFeedbackFields) {
      this.applyCriteriaWarning(formValue.overallPercentage, formValue.status as RecruitmentStatus);
    }
    if (!this.isValidStageChange(formValue.status as RecruitmentStatus, formValue.interviewRound)) {
      return;
    }

    const keepPreOfferDetails = this.isOfferedOrLater;
    const reviewedOn = this.formatReviewedOn(formValue.reviewedOn);
    const canEditAssignment = this.isHrUser;
    const feedbackFieldsVisible = this.showInterviewerFeedbackFields;
    const departmentId = formValue.departmentId || '';
    const departmentName = this.getDepartmentNameById(departmentId);
    const updatedCandidate: RecruitmentCandidate = {
      ...this.selectedCandidate,
      reviewed: canEditAssignment ? formValue.reviewed : this.selectedCandidate.reviewed,
      reviewedOn: canEditAssignment && !keepPreOfferDetails ? reviewedOn || this.selectedCandidate.reviewedOn : this.selectedCandidate.reviewedOn,
      cvUploadedOn: this.selectedCandidate.cvUploadedOn,
      reviewedBy: canEditAssignment ? formValue.reviewedBy || '-' : this.selectedCandidate.reviewedBy,
      sourceOfResume: canEditAssignment && !keepPreOfferDetails ? formValue.sourceOfResume || '' : this.selectedCandidate.sourceOfResume,
      departmentId: canEditAssignment ? departmentId : this.selectedCandidate.departmentId,
      departmentName: canEditAssignment ? departmentName : this.selectedCandidate.departmentName,
      interviewerName: canEditAssignment ? formValue.interviewerName || '-' : this.selectedCandidate.interviewerName,
      interviewRound: canEditAssignment ? formValue.interviewRound || '-' : this.selectedCandidate.interviewRound,
      technicalSkills: formValue.technicalSkills || this.selectedCandidate.technicalSkills || '',
      overallPercentage: keepPreOfferDetails || !feedbackFieldsVisible
        ? this.selectedCandidate.overallPercentage
        : formValue.overallPercentage === '' || formValue.overallPercentage === null
        ? this.selectedCandidate.overallPercentage
        : Number(formValue.overallPercentage),
      recommendationStatus: keepPreOfferDetails
        ? this.selectedCandidate.recommendationStatus || ''
        : feedbackFieldsVisible
        ? formValue.recommendationStatus || this.selectedCandidate.recommendationStatus || ''
        : this.selectedCandidate.recommendationStatus || '',
      status: formValue.status as RecruitmentStatus,
      remarks: formValue.remarks || ''
    };

    this.subscriptions.add(
      this.recruitmentService.updateCandidate(updatedCandidate).subscribe((candidate) => {
        this.selectedCandidate = candidate;
        this.loadCandidates();
        if (this.isHrUser) {
          this.loadStats();
        }
        this.showView('upload');
      })
    );
  }

  openDocuments(candidate: RecruitmentCandidate): void {
    this.selectedDocumentCandidate = candidate;
    this.activeView = 'documents';
  }

  getStatusClass(status: RecruitmentStatus): string {
    return `status-${status.toLowerCase()}`;
  }

  getStatusLabel(status: RecruitmentStatus): string {
    return this.statusOptions.find((option) => option.value === status)?.label ?? status;
  }

  canManageDocuments(status: RecruitmentStatus): boolean {
    return ['OFFERED', 'MSA_SENT', 'MSA_ACCEPTED', 'JOINED'].includes(status);
  }

  getUserDisplayName(user: IdName): string {
    return user.name || '-';
  }

  getUserNameById(userId: number | null): string {
    if (!userId) {
      return '-';
    }
    return [...this.hrUsers, ...this.employeeUsers, ...this.hrDepartmentUsers].find((user) => Number(user.id) === Number(userId))?.name || '-';
  }

  getInterviewerOptions(): IdName[] {
    return this.employeeUsers;
  }

  getInterviewerLabel(): string {
    return this.isPostOfferFlow ? 'Concern Name' : 'Interviewer Name';
  }

  getReviewStatusOptions(): { value: RecruitmentStatus; label: string }[] {
    if (this.isHrUser && this.selectedCandidate?.status === 'REJECTED') {
      return PRE_OFFER_STATUS_OPTIONS.filter((option) => option.value === 'RESCHEDULED');
    }
    const options = !this.isHrUser
      ? INTERVIEWER_STATUS_OPTIONS
      : this.isPostOfferFlow ? POST_OFFER_STATUS_OPTIONS : PRE_OFFER_STATUS_OPTIONS;
    return this.filterStatusOptionsByRound(options);
  }

  getHistoryField(row: RecruitmentChangeHistory, label: string): string {
    return this.extractHistoryValue(row.remarks, label) || '-';
  }

  getHistoryDepartment(row: RecruitmentChangeHistory): string {
    const historyDepartment = this.extractHistoryValue(row.remarks, 'Department');
    if (historyDepartment) {
      return historyDepartment;
    }

    const rowDepartmentName = row.departmentName;
    if (rowDepartmentName) {
      return rowDepartmentName;
    }

    const rowDepartment = this.getDepartmentNameById(row.departmentId);
    if (rowDepartment !== '-') {
      return rowDepartment;
    }

    const candidate = this.candidates.find((item) => Number(item.id) === Number(row.candidateId));
    return candidate?.departmentName || this.getDepartmentNameById(candidate?.departmentId);
  }

  getHistoryRemarks(row: RecruitmentChangeHistory): string {
    const remarks = this.extractHistoryValue(row.remarks, 'Remarks');
    if (remarks) {
      return remarks;
    }
    return row.remarks && !row.remarks.includes(':') ? row.remarks : '-';
  }

  getHistoryChangedOn(row: RecruitmentChangeHistory): string {
    return this.extractHistoryValue(row.remarks, 'Reviewed On') || row.createdDate || '-';
  }

  exportToExcel(): void {
    this.subscriptions.add(
      this.recruitmentService.exportChangeHistory(this.exportSearchText.trim()).subscribe((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'recruitment-change-history.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
      })
    );
  }

  private loadCandidates(): void {
    const interviewerName = this.isHrUser ? undefined : this.currentUserName();
    this.subscriptions.add(
      this.recruitmentService.getCandidates({
        search: this.searchText.trim() || undefined,
        status: this.statusFilter === 'All' ? undefined : this.statusFilter as RecruitmentStatus,
        interviewerName,
        page: this.candidatePageIndex,
        size: this.candidatePageSize
      }).subscribe((response) => {
        const candidates = this.isHrUser
          ? response.candidates
          : response.candidates.filter((candidate) => candidate.interviewerName?.trim().toLowerCase() === interviewerName?.trim().toLowerCase());
        this.candidates = candidates;
        this.filteredCandidates = candidates;
        this.totalRecords = this.isHrUser ? response.totalRecords : candidates.length;

        if (this.selectedCandidate) {
          this.selectedCandidate = candidates.find((candidate) => candidate.id === this.selectedCandidate?.id) ?? this.selectedCandidate;
        }
        if (this.selectedDocumentCandidate) {
          this.selectedDocumentCandidate = candidates.find((candidate) => candidate.id === this.selectedDocumentCandidate?.id) ?? this.selectedDocumentCandidate;
        }
        if (this.activeView === 'upload' && this.shouldScrollCandidateListOnLoad) {
          this.shouldScrollCandidateListOnLoad = false;
          this.scrollToCandidateList();
        }
      })
    );
  }

  private loadStats(): void {
    if (!this.isHrUser) {
      return;
    }
    this.subscriptions.add(
      this.recruitmentService.getStats().subscribe((stats) => {
        this.stats = stats;
      })
    );
  }

  private loadReviewUsers(): void {
    this.hrUsers = [this.currentUserOption()];

    this.subscriptions.add(
      this.userService.getUserIdNameList().subscribe((response) => {
        this.employeeUsers = response.responseObject ?? [];
      })
    );
    this.subscriptions.add(
      this.userService.getDepartments().subscribe((departments) => {
        this.departments = this.withManagementDepartment(departments);
        this.patchSelectedCandidateDepartment();
        this.loadHrRoleUsers();
      })
    );
  }

  private loadHrRoleUsers(): void {
    this.subscriptions.add(
      this.userService.getRoles().subscribe((roles) => {
        const hrRoleId = roles.find((role) => role.role?.toLowerCase() === 'hr')?.id ?? 4;
        this.subscriptions.add(
          this.userService.getAllUsers(Number(this.currentUser()?.id ?? 0), 1, 1000, undefined, undefined, [hrRoleId]).subscribe((users) => {
            this.hrDepartmentUsers = users.map((user) => this.toIdName(user));
          })
        );
      })
    );
  }

  private loadChangeHistory(): void {
    this.subscriptions.add(
      this.recruitmentService.getChangeHistory().subscribe((rows) => {
        this.changeHistory = rows;
        this.exportPageIndex = 0;
      })
    );
  }

  private loadCandidateHistory(candidateId: number): void {
    this.subscriptions.add(
      this.recruitmentService.getCandidateHistory(candidateId).subscribe((rows) => {
        this.candidateHistory = rows;
      })
    );
  }

  private loadCandidateDetails(candidateId: number): void {
    this.subscriptions.add(
      this.recruitmentService.getCandidate(candidateId).subscribe((candidate) => {
        this.selectedCandidate = candidate;
        this.patchCandidateForm(candidate);
        this.applyCandidateFormAccess();
      })
    );
  }

  private currentUserOption(): IdName {
    const user = this.currentUser();
    return {
      id: Number(user?.id ?? 0),
      name: this.currentUserName()
    };
  }

  private applyCandidateFormAccess(): void {
    this.candidateForm.enable({ emitEvent: false });
    this.candidateForm.get('cvUploadedOn')?.disable({ emitEvent: false });
    if (this.isOfferedOrLater) {
      ['sourceOfResume', 'reviewedOn', 'overallPercentage', 'recommendationStatus'].forEach((controlName) => {
        this.candidateForm.get(controlName)?.disable({ emitEvent: false });
      });
    }
    if (this.isHrUser) {
      this.applyFeedbackValidators();
      return;
    }

    Object.keys(this.candidateForm.controls).forEach((controlName) => {
      this.candidateForm.get(controlName)?.disable({ emitEvent: false });
    });
    ['status', 'overallPercentage', 'recommendationStatus', 'remarks'].forEach((controlName) => {
      this.candidateForm.get(controlName)?.enable({ emitEvent: false });
    });
    this.applyFeedbackValidators();
  }

  private applyFeedbackValidators(): void {
    const recommendationControl = this.candidateForm.get('recommendationStatus');
    const remarksControl = this.candidateForm.get('remarks');
    recommendationControl?.setValidators(this.showInterviewerFeedbackFields ? [Validators.required] : []);
    remarksControl?.setValidators([Validators.required]);
    recommendationControl?.updateValueAndValidity({ emitEvent: false });
    remarksControl?.updateValueAndValidity({ emitEvent: false });
  }

  private ensureHrUserOption(name: string): void {
    if (!name || name === '-' || this.hrUsers.some((user) => user.name === name)) {
      return;
    }
    this.hrUsers = [...this.hrUsers, { id: 0, name }];
  }

  private onDepartmentChange(departmentId: number | string): void {
    if (this.isPostOfferFlow) {
      return;
    }
    this.candidateForm.get('interviewerName')?.setValue('', { emitEvent: false });
  }

  private patchSelectedCandidateDepartment(): void {
    if (!this.selectedCandidate || this.activeView !== 'review') {
      return;
    }

    this.ensureCandidateDepartmentOption(this.selectedCandidate);
    this.candidateForm.get('departmentId')?.setValue(
      this.resolveCandidateDepartmentId(this.selectedCandidate),
      { emitEvent: false }
    );
  }

  private resolveCandidateDepartmentId(candidate: RecruitmentCandidate): number | string {
    if (candidate.departmentId !== undefined && candidate.departmentId !== null && candidate.departmentId !== '') {
      const departmentId = Number(candidate.departmentId);
      return Number.isNaN(departmentId) ? candidate.departmentId : departmentId;
    }

    const departmentName = candidate.departmentName?.trim().toLowerCase();
    if (!departmentName) {
      return '';
    }

    return this.departments.find((department) => department.departmentName?.trim().toLowerCase() === departmentName)?.id || '';
  }

  private ensureCandidateDepartmentOption(candidate: RecruitmentCandidate): void {
    if (!candidate.departmentName || this.departments.length === 0) {
      return;
    }

    const departmentName = candidate.departmentName.trim();
    const hasDepartment = this.departments.some((department) => department.departmentName?.trim().toLowerCase() === departmentName.toLowerCase());
    if (hasDepartment) {
      return;
    }

    const departmentId = Number(candidate.departmentId);
    this.departments = [
      ...this.departments,
      {
        id: Number.isNaN(departmentId) ? -Date.now() : departmentId,
        departmentName
      }
    ];
  }

  private getDepartmentNameById(departmentId: number | string | undefined): string {
    if (departmentId === undefined || departmentId === null || departmentId === '') {
      return '-';
    }

    return this.departments.find((department) => Number(department.id) === Number(departmentId))?.departmentName || '-';
  }

  private onStatusChange(status: RecruitmentStatus): void {
    if (!this.isHrUser || !['SCHEDULED', 'RESCHEDULED'].includes(status) || this.candidateForm.get('reviewedOn')?.value) {
      return;
    }

    this.candidateForm.get('reviewedOn')?.setValue(new Date(), { emitEvent: false });
  }

  private onInterviewRoundChange(round: string): void {
    this.applyFeedbackValidators();
    if (round === 'Screening') {
      this.candidateForm.get('interviewerName')?.setValue('', { emitEvent: false });
      this.candidateForm.get('reviewedOn')?.setValue('', { emitEvent: false });
    }
    if (round !== 'Screening' && this.candidateForm.get('status')?.value === 'SCREEN_PAST') {
      this.candidateForm.get('status')?.setValue('', { emitEvent: false });
    }
  }

  private toIdName(user: UserDTO): IdName {
    return {
      id: Number(user.id ?? 0),
      name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.userName || user.email || '-'
    };
  }

  private withManagementDepartment(departments: Department[]): Department[] {
    if (departments.some((department) => department.departmentName?.toLowerCase() === 'management')) {
      return departments;
    }

    return [...departments, { id: DepartmentEnum.MANAGEMENT, departmentName: 'Management' }];
  }

  private currentUserName(): string {
    const user = this.currentUser();
    const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    return fullName || user?.userName || user?.email || '-';
  }

  private currentUser(): any {
    const user = sessionStorage.getItem('userData');
    if (!user) {
      return null;
    }

    try {
      return JSON.parse(user);
    } catch {
      return null;
    }
  }

  private toDateControlValue(value: string): Date | string {
    if (!value || value === '-') {
      return '';
    }

    const dateParts = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dateParts) {
      return new Date(Number(dateParts[3]), Number(dateParts[2]) - 1, Number(dateParts[1]));
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed;
  }

  private toDateTimeControlValue(value: Date | string): Date | string {
    if (!value || value === '-') {
      return '';
    }

    const parsed = value instanceof Date ? value : this.parseDateTime(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed;
  }

  private parseDateTime(value: string): Date {
    const dateTimeParts = value.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
    if (dateTimeParts) {
      return new Date(
        Number(dateTimeParts[3]),
        Number(dateTimeParts[2]) - 1,
        Number(dateTimeParts[1]),
        Number(dateTimeParts[4] ?? 0),
        Number(dateTimeParts[5] ?? 0)
      );
    }

    return new Date(value);
  }

  private formatReviewedOn(value: Date | string | { toDate?: () => Date }): string {
    if (!value) {
      return '';
    }

    if (value instanceof Date) {
      return this.datePipe.transform(value, 'dd-MM-yyyy HH:mm') ?? '';
    }

    if (typeof value === 'object' && value.toDate) {
      return this.datePipe.transform(value.toDate(), 'dd-MM-yyyy HH:mm') ?? '';
    }

    if (typeof value !== 'string') {
      return '';
    }

    const parsed = this.parseDateTime(value);
    return Number.isNaN(parsed.getTime()) ? value : this.datePipe.transform(parsed, 'dd-MM-yyyy HH:mm') ?? '';
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private isValidStageChange(nextStatus: RecruitmentStatus, nextRound: string): boolean {
    const statusMessage = this.validateStatusChange(this.selectedCandidate?.status, nextStatus, this.selectedCandidate?.interviewRound, nextRound);
    if (statusMessage) {
      this.showMessage(statusMessage);
      return false;
    }

    const roundMessage = this.validateInterviewRoundChange(this.selectedCandidate?.interviewRound, nextRound);
    if (roundMessage) {
      this.showMessage(roundMessage);
      return false;
    }

    return true;
  }

  // private isOverallPercentageValid(): boolean {  need to add overall %  validation 
  //   alert("into thissss")
  //   if (!this.showInterviewerFeedbackFields) {
  //     return true;
  //   }

  //   const control = this.candidateForm.get('overallPercentage');
  //   if (!control || control.valid) {
  //     return true;
  //   }

  //   // control.markAsTouched();
  //   // this.showMessage('Overall percentage must be a number from 0 to 100.');
  //   return false;
  // }

  private applyCriteriaWarning(percentage: number | string | null, status: RecruitmentStatus): void {
    const score = Number(percentage);
    if (!Number.isFinite(score)) {
      return;
    }
    // if (score < DEFAULT_SELECTION_PERCENTAGE && ['SCREEN_PAST', 'PASS_CLEARED', 'SELECTED', 'OFFERED'].includes(status)) {
    //   alert("comes here")
    //   this.showMessage(`Overall percentage is below ${DEFAULT_SELECTION_PERCENTAGE}. HR can still make the final decision.`);
    // }
  }

  private filterStatusOptionsByRound(options: { value: RecruitmentStatus; label: string }[]): { value: RecruitmentStatus; label: string }[] {
    const round = this.candidateForm?.get('interviewRound')?.value || this.selectedCandidate?.interviewRound;
    if (round === 'Screening') {
      return RECRUITMENT_STATUS_OPTIONS.filter((option) => ['SCREEN_PAST', 'ON_HOLD', 'REJECTED'].includes(option.value));
    }
    return options.filter((option) => option.value !== 'SCREEN_PAST' || round === 'Screening');
  }

  private validateStatusChange(currentStatus?: RecruitmentStatus, nextStatus?: RecruitmentStatus, currentRound?: string, nextRound?: string): string {
    if (!currentStatus || !nextStatus || currentStatus === nextStatus) {
      return '';
    }

    if (currentStatus === 'REJECTED') {
      return this.isHrUser && nextStatus === 'RESCHEDULED' ? '' : RECRUITMENT_MESSAGES.rejectedLocked;
    }

    if (currentStatus === 'JOINED') {
      return RECRUITMENT_MESSAGES.joinedLocked;
    }

    if (nextStatus === 'REJECTED') {
      return '';
    }

    if (this.isAllowedRoundSchedule(currentStatus, nextStatus, currentRound, nextRound)) {
      return '';
    }

    return STATUS_ORDER[nextStatus] < STATUS_ORDER[currentStatus] ? RECRUITMENT_MESSAGES.statusBackward : '';
  }

  private validateInterviewRoundChange(currentRound?: string, nextRound?: string): string {
    if (this.isHrUser && this.selectedCandidate?.status === 'REJECTED' && this.candidateForm.getRawValue().status === 'RESCHEDULED') {
      return '';
    }
    if (this.isHrUser && this.candidateForm.getRawValue().status === 'SCHEDULED') {
      return '';
    }
    if (this.isFlexibleDecisionRoundChange(currentRound, nextRound)) {
      return '';
    }
    const currentIndex = currentRound ? INTERVIEW_ROUND_ORDER[currentRound] : 0;
    const nextIndex = nextRound ? INTERVIEW_ROUND_ORDER[nextRound] : 0;
    return nextIndex && currentIndex && nextIndex < currentIndex ? RECRUITMENT_MESSAGES.interviewRoundBackward : '';
  }

  private isFlexibleDecisionRoundChange(currentRound?: string, nextRound?: string): boolean {
    const flexibleRounds = ['Final Interaction', 'HR Round'];
    return !!currentRound && !!nextRound && flexibleRounds.includes(currentRound) && flexibleRounds.includes(nextRound);
  }

  private isAllowedRoundSchedule(currentStatus: RecruitmentStatus, nextStatus: RecruitmentStatus, currentRound?: string, nextRound?: string): boolean {
    if (!this.isHrUser || !['SCHEDULED', 'RESCHEDULED'].includes(nextStatus)) {
      return false;
    }

    const currentIndex = currentRound ? INTERVIEW_ROUND_ORDER[currentRound] : 0;
    const nextIndex = nextRound ? INTERVIEW_ROUND_ORDER[nextRound] : 0;
    if (currentStatus === 'PASS_CLEARED' || currentStatus === 'SCREEN_PAST') {
      return nextIndex > currentIndex;
    }
    if (currentStatus === 'ON_HOLD') {
      return nextIndex >= currentIndex;
    }
    return false;
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

  private scrollToCandidateList(): void {
    setTimeout(() => {
      this.candidateListSection?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }

  private isPostOfferStatus(status?: RecruitmentStatus): boolean {
    return !!status && STATUS_ORDER[status] >= STATUS_ORDER.OFFERED && status !== 'REJECTED';
  }

  private extractHistoryValue(remarks: string, label: string): string {
    if (!remarks) {
      return '';
    }

    const entry = remarks
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.toLowerCase().startsWith(`${label.toLowerCase()}:`));

    if (!entry) {
      return '';
    }

    const value = entry.substring(entry.indexOf(':') + 1).trim();
    const nextValue = value.includes('->') ? value.split('->').pop()?.trim() : value;
    return nextValue && nextValue !== '-' ? nextValue : '';
  }

  private paginate<T>(items: T[], pageIndex: number, pageSize: number): T[] {
    const start = pageIndex * pageSize;
    return items.slice(start, start + pageSize);
  }
}
